import { create } from 'zustand'

export type ShellTab = 'broadcasts' | 'chats' | 'groups'
export type ShellView = 'composer' | 'thread' | 'delivery' | 'group'
export type ConversationKind = 'broadcast' | 'chat'
/**
 * A group isn't a conversation, but it occupies the same rail-selection slot,
 * so the two share selectedId rather than keeping a second copy of "what is
 * highlighted" that could disagree.
 */
export type SelectedKind = ConversationKind | 'group'
export type MobileView = 'list' | 'detail'

interface ShellState {
  tab: ShellTab
  view: ShellView
  selectedId: string | null
  selectedKind: SelectedKind | null
  listQuery: string
  mobileView: MobileView
  newChatOpen: boolean
  templatesOpen: boolean
  numberPoolOpen: boolean
  departmentsOpen: boolean
  /**
   * Groups tab: show only groups that would leave somebody out. The whole point
   * of the screen is catching those, so it's worth being one click away.
   */
  unreachableOnly: boolean

  setTab: (tab: ShellTab) => void
  setListQuery: (q: string) => void
  openComposer: () => void
  selectConversation: (id: string, kind: ConversationKind) => void
  selectGroup: (id: string) => void
  setUnreachableOnly: (only: boolean) => void
  openDelivery: () => void
  backToThread: () => void
  backToList: () => void
  openNewChat: () => void
  closeNewChat: () => void
  openTemplates: () => void
  closeTemplates: () => void
  openNumberPool: () => void
  closeNumberPool: () => void
  openDepartments: () => void
  closeDepartments: () => void
}

// Client-side inbox state machine that drives the rail + main pane (mirrors the
// prototype's tab / selectedId / view / mobileView logic). Composer-local state
// (chips, message, priority) lives inside the Composer component.
export const useShellStore = create<ShellState>((set) => ({
  tab: 'broadcasts',
  view: 'composer', // compose-first default right pane
  selectedId: null,
  selectedKind: null,
  listQuery: '',
  mobileView: 'list',
  newChatOpen: false,
  templatesOpen: false,
  numberPoolOpen: false,
  departmentsOpen: false,
  unreachableOnly: false,

  setTab: (tab) => set({ tab, listQuery: '' }),
  setListQuery: (listQuery) => set({ listQuery }),
  // Starting or opening something also flips the rail to that kind, so the list
  // beside you never disagrees with the pane you're looking at.
  openComposer: () =>
    set({
      view: 'composer',
      selectedId: null,
      selectedKind: null,
      mobileView: 'detail',
      tab: 'broadcasts',
    }),
  selectConversation: (selectedId, selectedKind) =>
    set({
      selectedId,
      selectedKind,
      view: 'thread',
      mobileView: 'detail',
      tab: selectedKind === 'chat' ? 'chats' : 'broadcasts',
    }),
  selectGroup: (selectedId) =>
    set({
      selectedId,
      selectedKind: 'group',
      view: 'group',
      mobileView: 'detail',
      tab: 'groups',
    }),
  setUnreachableOnly: (unreachableOnly) => set({ unreachableOnly }),
  openDelivery: () => set({ view: 'delivery' }),
  backToThread: () => set({ view: 'thread' }),
  backToList: () => set({ mobileView: 'list' }),
  openNewChat: () => set({ newChatOpen: true, tab: 'chats' }),
  closeNewChat: () => set({ newChatOpen: false }),
  openTemplates: () => set({ templatesOpen: true }),
  closeTemplates: () => set({ templatesOpen: false }),
  openNumberPool: () => set({ numberPoolOpen: true }),
  closeNumberPool: () => set({ numberPoolOpen: false }),
  openDepartments: () => set({ departmentsOpen: true }),
  closeDepartments: () => set({ departmentsOpen: false }),
}))
